import './Calendar.scss';

import { addDays, format, isSameDay } from 'date-fns';
import * as pickerLocales from 'date-fns/locale';
import { getCalendarLocaleKey } from '../../utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { TCalendarProps } from '../../utils/types';
import { useRecoilValue } from 'recoil';
import { fullDataAtom } from '../../atoms';

const DAYS_AHEAD = 90;

const Calendar = ({ date, setDate }: TCalendarProps) => {
  const [locale, setLocale] = useState(pickerLocales.enUS);
  const fullExpertData = useRecoilValue(fullDataAtom);
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [visibleIndex, setVisibleIndex] = useState(0);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const activeDays = useMemo(() => {
    const schedule = fullExpertData?.workHours?.schedule;
    if (!schedule) return [1, 2, 3, 4, 5];

    const daysMap: Array<{ key: string; index: number }> = [
      { key: 'SUNDAY', index: 0 },
      { key: 'MONDAY', index: 1 },
      { key: 'TUESDAY', index: 2 },
      { key: 'WEDNESDAY', index: 3 },
      { key: 'THURSDAY', index: 4 },
      { key: 'FRIDAY', index: 5 },
      { key: 'SATURDAY', index: 6 },
    ];

    const enabled = daysMap
      .filter(({ key }) => schedule[key as keyof typeof schedule]?.enabled === true)
      .map(({ index }) => index);

    return enabled.length > 0 ? enabled : [1, 2, 3, 4, 5];
  }, [fullExpertData]);

  const availableDates = useMemo(() => {
    const freeHours = fullExpertData?.freeHours?.freeHours;
    if (!freeHours) return null;

    const dates = new Set<string>();
    Object.entries(freeHours).forEach(([dateStr, slots]) => {
      if (slots.length > 0 && slots.some(slot => !slot.blocked)) {
        dates.add(dateStr);
      }
    });

    return dates;
  }, [fullExpertData]);

  useEffect(() => {
    const userLocale = getCalendarLocaleKey() as keyof typeof pickerLocales;
    const localeToSet = pickerLocales[userLocale] || pickerLocales.enUS;
    setLocale(localeToSet);
  }, []);

  const toLocalDateStr = (d: Date): string => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const isDateSelectable = (checkDate: Date): boolean => {
    if (availableDates !== null) {
      return availableDates.has(toLocalDateStr(checkDate));
    }
    return activeDays.includes(checkDate.getDay());
  };

  // only future (and today) dates are ever generated, so past days never appear in the strip
  const days = useMemo(() => Array.from({ length: DAYS_AHEAD }, (_, i) => addDays(today, i)), [today]);

  const visibleMonthLabel = useMemo(() => {
    const visibleDate = days[visibleIndex];
    return visibleDate ? format(visibleDate, 'LLLL yyyy', { locale }) : '';
  }, [days, visibleIndex, locale]);

  useEffect(() => {
    if (!date) return;
    const index = days.findIndex(d => isSameDay(d, date));
    const el = itemRefs.current[index];
    el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    // only run once, on mount / when the selected date changes externally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // React attaches JSX onWheel as a passive listener, so preventDefault() there is a no-op —
    // a native listener is required to turn vertical mouse-wheel scrolling into horizontal scroll here.
    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      container.scrollLeft += e.deltaY;
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  const scrollTicking = useRef(false);

  const handleScroll = () => {
    if (scrollTicking.current) return;
    scrollTicking.current = true;

    requestAnimationFrame(() => {
      scrollTicking.current = false;

      const container = scrollRef.current;
      if (!container) return;

      const scrollLeft = container.scrollLeft;
      let closestIndex = 0;

      for (let i = 0; i < itemRefs.current.length; i++) {
        const el = itemRefs.current[i];
        if (!el) continue;
        if (el.offsetLeft <= scrollLeft + 4) {
          closestIndex = i;
        } else {
          break;
        }
      }

      setVisibleIndex(closestIndex);
    });
  };

  const scrollByMonth = (direction: 1 | -1) => {
    const current = days[visibleIndex];
    if (!current) return;

    const targetMonth = current.getMonth() + direction;
    const targetYear = current.getFullYear() + Math.floor(targetMonth / 12);
    const normalizedMonth = ((targetMonth % 12) + 12) % 12;

    let targetIndex = days.findIndex(d => d.getMonth() === normalizedMonth && d.getFullYear() === targetYear);

    if (targetIndex === -1) {
      // the target month falls outside the generated range — snap to the nearest edge instead
      targetIndex = direction === -1 ? 0 : days.length - 1;
    }

    itemRefs.current[targetIndex]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  return (
    <div className='dateStrip'>
      <div className='dateStrip__header'>
        <button
          type='button'
          className='dateStrip__nav'
          onClick={() => scrollByMonth(-1)}
          aria-label='scroll to previous month'>
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
            <path d='M15 18l-6-6 6-6' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </button>
        <p className='dateStrip__month'>{visibleMonthLabel}</p>
        <button
          type='button'
          className='dateStrip__nav'
          onClick={() => scrollByMonth(1)}
          aria-label='scroll to next month'>
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
            <path d='M9 6l6 6-6 6' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </button>
      </div>
      <div className='dateStrip__scroller' ref={scrollRef} onScroll={handleScroll}>
        {days.map((d, i) => {
          const selectable = isDateSelectable(d);
          const isSelected = !!date && isSameDay(d, date);
          const isToday = isSameDay(d, today);

          return (
            <button
              key={toLocalDateStr(d)}
              ref={el => {
                itemRefs.current[i] = el;
              }}
              type='button'
              className={`dateStrip__day${isSelected ? ' dateStrip__day--selected' : ''}${
                isToday && !isSelected ? ' dateStrip__day--today' : ''
              }`}
              disabled={!selectable}
              onClick={() => setDate(d)}>
              <span className='dateStrip__weekday'>{format(d, 'EEEEEE', { locale })}</span>
              <span className='dateStrip__number'>{format(d, 'd')}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
